import COMPANY_STATE from '@/guards/CompanyState.gql'
import COMPANY_BASE from '@/queries/CompanyBase.gql'

/**
 * True when the company should use onboarding navigation (not yet active).
 * ACTIVE in stringValues wins over a stale ONBOARDING entry in the Apollo cache.
 */
export function isCompanyInOnboarding(stringValues) {
  if (!stringValues?.length) return false
  if (stringValues.includes('ACTIVE')) return false
  return stringValues.includes('ONBOARDING')
}

/** Drop stale ONBOARDING when the backend has already activated the company. */
export function normalizeCompanyStateValues(stringValues) {
  if (!stringValues?.length) return []
  const values = stringValues.filter(Boolean)
  if (values.includes('ACTIVE')) {
    return values.filter((value) => value !== 'ONBOARDING')
  }
  return values
}

/**
 * After submitOnboarding, sync state into every cached Company (e.g. CompanyBase in nav / App).
 * Company.state is typed as `State` in GraphQL — not `CompanyState` from the mutation payload.
 */
export function syncCompanyStateInApolloCache(apolloClient, companyId, stringValues) {
  const normalized = normalizeCompanyStateValues(stringValues)
  if (!companyId || !normalized.length) return false

  const cacheId = apolloClient.cache.identify({ __typename: 'Company', id: companyId })
  if (!cacheId) return false

  apolloClient.cache.modify({
    id: cacheId,
    fields: {
      state() {
        return {
          __typename: 'State',
          stringValues: [...normalized],
        }
      },
    },
  })
  return true
}

/** Refresh company state everywhere navigation and guards read it. */
export async function refetchCompanyStateInApolloCache(apolloClient, companyId) {
  if (!companyId) return

  await Promise.all([
    apolloClient.query({
      query: COMPANY_BASE,
      variables: { id: companyId },
      fetchPolicy: 'network-only',
    }),
    apolloClient.query({
      query: COMPANY_STATE,
      variables: { id: companyId },
      fetchPolicy: 'network-only',
    }),
  ])
}
